import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteEquipment, EQUIPMENT_QUERY_KEY } from './CarrierProfile.service'
import { type Equipment, WeightUnit } from './CarrierProfile.types'
import CreateOrEditEquipmentDialog from './CreateOrEditEquipmentDialog'

const getWeightUnitLabel = (weightUnit: WeightUnit): string => {
  switch (weightUnit) {
    case WeightUnit.KILOGRAMS: {
      return 'kg'
    }
    case WeightUnit.METRIC_TONNES: {
      return 't'
    }
    case WeightUnit.TONS: {
      return 'ton'
    }
    case WeightUnit.POUNDS: {
      return 'lbs'
    }
    case WeightUnit.OTHER:
    default :
      return ''
  }
}

interface EquipmentTableProps {
  readonly equipment: Equipment[]
}

const EquipmentTable = ({
  equipment,
}: EquipmentTableProps): ReactElement => {
  const { t } = useTranslation()
  const [activeEquipment, setActiveEquipment] = useState<Equipment>()
  const queryClient = useQueryClient()
  const deleteEquipmentMutation = useMutation({
    mutationFn: deleteEquipment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EQUIPMENT_QUERY_KEY] }),
  })
  const editEquipmentDialog = useDialog<typeof CreateOrEditEquipmentDialog>()

  const handleEditClick = useCallback((row: Equipment) => {
    setActiveEquipment(row)
    editEquipmentDialog.handleOpen()
  }, [])

  const handleDeleteEquipmentDialogAction = useCallback(async () => {
    if (!activeEquipment) {
      return
    }
    await deleteEquipmentMutation.mutateAsync(activeEquipment.id)
    analyticsEvent('polestar_carrier_profile_equipment_removed')
  }, [activeEquipment])

  const deleteEquipmentDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: handleDeleteEquipmentDialogAction,
  })

  const handleDeleteClick = useCallback((row: Equipment) => {
    setActiveEquipment(row)
    deleteEquipmentDialog.handleOpen()
  }, [])

  const actions = useMemo((): KNDataTableAction<Equipment>[] => [
    {
      name: 'edit',
      label: t('screens.cs.carrier_profile.equipment.edit_equipment'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('general.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
    },
  ], [])

  const equipmentTypeColumnValue = (row: Equipment): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {t(`screens.cs.carrier_profile.equipment.equipment_enum.${row.type}`)}
    </Typography>
  )

  const weightCapacityColumnValue = (row: Equipment): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {row.weightCapacity} {getWeightUnitLabel(row.weightUnit)}
    </Typography>
  )

  const fuelTypeColumnValue = (row: Equipment): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {t(`screens.cs.carrier_profile.equipment.fuel_enum.${row.fuelType}`)}
    </Typography>
  )

  const quantityColumnValue = (row: Equipment): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {row.quantity}
    </Typography>
  )

  const columns = useMemo((): KNDataTableColumn<Equipment>[] => [
    {
      name: 'equipment',
      label: t('screens.cs.carrier_profile.equipment.equipment'),
      getValue: equipmentTypeColumnValue,
      sx: { width: { xs: '10rem', xxl: 'auto' } },
    },
    {
      name: 'weight',
      label: t('screens.cs.carrier_profile.equipment.weight'),
      getValue: weightCapacityColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'fuelType',
      label: t('screens.cs.carrier_profile.equipment.fuel_type'),
      getValue: fuelTypeColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'quantity',
      label: t('screens.cs.carrier_profile.equipment.quantity'),
      getValue: quantityColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={actions}
        data={equipment}
        sx={{
          marginX: -2,
        }}
      />
      {activeEquipment && (
        <>
          <CreateOrEditEquipmentDialog
            payload={{
              equipment: activeEquipment,
            }}
            open={editEquipmentDialog.isOpen}
            onAction={editEquipmentDialog.handleAction}
            onClose={editEquipmentDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.carrier_profile.equipment.delete_confirmation')}
            continueLabel={t('general.delete')}
            open={deleteEquipmentDialog.isOpen}
            onAction={deleteEquipmentDialog.handleAction}
            onClose={deleteEquipmentDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default EquipmentTable
